import React, { useState, useMemo, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import styled from "styled-components";

const PAGE_SIZE = 10;

type Tab = "volumes" | "filter-freq" | "attr-freq";


type VolumeItem = {
  campaign_name: string;
  differential_privacy_count_total: number;
  filter_str?: string[];
};

type Props = {
  data: VolumeItem[];
  onClose: () => void;
  loading?: boolean;
  error?: string | null;
};

const formatNum = (value: number) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return `${value}`;
};

const VolumeTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const item: VolumeItem = payload[0].payload;
  return (
    <TooltipBox>
      <TooltipName>{item.campaign_name}</TooltipName>
      <TooltipCount>
        Audience size:{" "}
        <strong>
          {Number(item.differential_privacy_count_total).toLocaleString()}
        </strong>
      </TooltipCount>
      {item.filter_str && item.filter_str.length > 0 && (
        <>
          <TooltipFilterLabel>Filter strings:</TooltipFilterLabel>
          {item.filter_str.map((f) => (
            <TooltipFilter key={f}>{f}</TooltipFilter>
          ))}
        </>
      )}
    </TooltipBox>
  );
};

const AttrTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;
  return (
    <TooltipBox>
      <TooltipName>{item.attr}</TooltipName>
      <TooltipCount>
        Filter strings: <strong>{item.filterCount}</strong>
      </TooltipCount>
      <TooltipCount>
        Campaigns: <strong>{item.campaignCount}</strong>
      </TooltipCount>
    </TooltipBox>
  );
};

function computeFilterFrequency(data: VolumeItem[]) {
  const map = new Map<string, { count: number; campaigns: Set<string> }>();
  for (const item of data) {
    const uniqueFilters = Array.from(new Set(item.filter_str ?? []));
    for (const f of uniqueFilters) {
      const entry = map.get(f) ?? { count: 0, campaigns: new Set<string>() };
      entry.count += 1;
      entry.campaigns.add(item.campaign_name);
      map.set(f, entry);
    }
  }
  return Array.from(map.entries())
    .map(([filter, { count, campaigns }]) => ({
      filter,
      count,
      campaigns: Array.from(campaigns),
    }))
    .sort((a, b) => b.count - a.count);
}

function computeAttrFrequency(data: VolumeItem[]) {
  const map = new Map<
    string,
    { filterCount: number; campaigns: Set<string> }
  >();
  for (const item of data) {
    for (const f of item.filter_str ?? []) {
      const attrs = Array.from(f.matchAll(/\b([a-z][a-z_]*)\s*=/gi))
        .map((m) => m[1].toLowerCase())
        .filter((a) => a !== "or" && a !== "and");
      const unique = Array.from(new Set(attrs));
      for (const attr of unique) {
        const entry = map.get(attr) ?? {
          filterCount: 0,
          campaigns: new Set<string>(),
        };
        entry.filterCount += 1;
        entry.campaigns.add(item.campaign_name);
        map.set(attr, entry);
      }
    }
  }
  return Array.from(map.entries())
    .map(([attr, { filterCount, campaigns }]) => ({
      attr,
      filterCount,
      campaignCount: campaigns.size,
    }))
    .sort((a, b) => b.filterCount - a.filterCount);
}

type TickTooltip = { x: number; y: number; text: string } | null;

type XAxisTickProps = {
  x?: number;
  y?: number;
  payload?: { value: string };
  onTooltip: (t: TickTooltip) => void;
};

const CustomXAxisTick: React.FC<XAxisTickProps> = ({
  x = 0,
  y = 0,
  payload,
  onTooltip,
}) => {
  const full = payload?.value ?? "";
  const label = full.length > 14 ? full.slice(0, 14) + "…" : full;
  const truncated = full.length > 14;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={10}
        textAnchor="end"
        fill="#667085"
        fontSize={11}
        style={{ fontFamily: "var(--font-data)" }}
        transform="rotate(-45)"
        onMouseEnter={
          truncated
            ? (e) => {
                const rect = (
                  e.target as SVGTextElement
                ).getBoundingClientRect();
                onTooltip({
                  x: rect.left + rect.width / 2,
                  y: rect.bottom,
                  text: full,
                });
              }
            : undefined
        }
        onMouseLeave={truncated ? () => onTooltip(null) : undefined}
      >
        {label}
      </text>
    </g>
  );
};

const TABS: { id: Tab; label: string }[] = [
  { id: "volumes", label: "Audience Volumes" },
  { id: "filter-freq", label: "Filter Frequency" },
  { id: "attr-freq", label: "Attribute Usage" },
];

const CampaignVolumesModal: React.FC<Props> = ({
  data,
  onClose,
  loading,
  error,
}) => {
  const [tab, setTab] = useState<Tab>("volumes");
  const [page, setPage] = useState(0);
  const [searchVolumes, setSearchVolumes] = useState("");
  const [searchAttr, setSearchAttr] = useState("");
  const [tickTooltip, setTickTooltip] = useState<TickTooltip>(null);
  const [showZeroCampaigns, setShowZeroCampaigns] = useState(false);

  const barColor = useMemo(
    () =>
      getComputedStyle(document.documentElement)
        .getPropertyValue("--Primary-Color-Shade-1")
        .trim() || "#0d6ab2",
    [],
  );

  const handleTickTooltip = useCallback(
    (t: TickTooltip) => setTickTooltip(t),
    [],
  );

  useEffect(() => {
    setPage(0);
  }, [data, searchVolumes]);

  // #1: derive search-filtered pool first, then zero-volume split, then sort
  const searchFilteredData = useMemo(
    () =>
      data.filter((d) =>
        searchVolumes.trim()
          ? d.campaign_name
              .toLowerCase()
              .includes(searchVolumes.toLowerCase())
          : true,
      ),
    [data, searchVolumes],
  );

  const sortedData = useMemo(
    () =>
      [...searchFilteredData]
        .filter((d) => d.differential_privacy_count_total > 0)
        .sort(
          (a, b) =>
            b.differential_privacy_count_total -
            a.differential_privacy_count_total,
        ),
    [searchFilteredData],
  );

  // #2: zero-volume list
  const zeroVolumeCampaigns = useMemo(
    () =>
      searchFilteredData.filter(
        (d) => d.differential_privacy_count_total === 0,
      ),
    [searchFilteredData],
  );

  // #1: stats now reflect the search-filtered view
  const volumeStats = useMemo(() => {
    const withVolume = searchFilteredData.filter(
      (d) => d.differential_privacy_count_total > 0,
    );
    const totalVolume = withVolume.reduce(
      (sum, d) => sum + d.differential_privacy_count_total,
      0,
    );
    const avg = withVolume.length
      ? Math.round(totalVolume / withVolume.length)
      : 0;
    return {
      totalCount: searchFilteredData.length,
      activeCount: withVolume.length,
      totalVolume,
      avg,
    };
  }, [searchFilteredData]);

  const filterFreqData = useMemo(() => computeFilterFrequency(data), [data]);
  const attrFreqData = useMemo(() => computeAttrFrequency(data), [data]);

  // #10: attr search filter
  const filteredAttrFreqData = useMemo(
    () =>
      searchAttr.trim()
        ? attrFreqData.filter((a) =>
            a.attr.toLowerCase().includes(searchAttr.toLowerCase()),
          )
        : attrFreqData,
    [attrFreqData, searchAttr],
  );

  const totalPages = useMemo(
    () => Math.ceil(sortedData.length / PAGE_SIZE),
    [sortedData.length],
  );

  const chartData = useMemo(
    () => sortedData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [sortedData, page],
  );

  const handleTabChange = useCallback((t: Tab) => {
    setTab(t);
    setPage(0);
    setTickTooltip(null);
  }, []);

  const xAxisTick = useCallback(
    (props: any) => (
      <CustomXAxisTick {...props} onTooltip={handleTickTooltip} />
    ),
    [handleTickTooltip],
  );

  return (
    <Backdrop onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Campaign Analytics</Title>
          <HeaderRight>
            <CloseBtn onClick={onClose}>✕</CloseBtn>
          </HeaderRight>
        </Header>

        <TabBar>
          {TABS.map((t) => (
            <TabItem
              key={t.id}
              active={tab === t.id}
              onClick={() => handleTabChange(t.id)}
            >
              {t.label}
            </TabItem>
          ))}
        </TabBar>

        {loading ? (
          <LoaderWrapper>
            <Spinner />
          </LoaderWrapper>
        ) : error ? (
          <LoaderWrapper>
            <ErrorText>{error}</ErrorText>
          </LoaderWrapper>
        ) : tab === "volumes" ? (
          <VolumesTab>
            <StatsRow>
              <StatCard>
                <StatValue>{formatNum(volumeStats.totalVolume)}</StatValue>
                <StatLabel>Total Matched</StatLabel>
              </StatCard>
              <StatDivider />
              <StatCard>
                <StatValue>{formatNum(volumeStats.avg)}</StatValue>
                <StatLabel>Avg per Campaign</StatLabel>
              </StatCard>
              <StatDivider />
              <StatCard>
                <StatValue>
                  {volumeStats.activeCount}
                  <StatCountTotal>
                    {" "}
                    / {volumeStats.totalCount}
                  </StatCountTotal>
                </StatValue>
                <StatLabel>Total Campaigns</StatLabel>
              </StatCard>
            </StatsRow>

            <SearchRow>
              <SearchInput
                type="search"
                placeholder="Filter campaigns…"
                value={searchVolumes}
                onChange={(e) => setSearchVolumes(e.target.value)}
              />
            </SearchRow>

            {data.length === 0 ? (
              <LoaderWrapper>
                <ErrorText>No campaigns found.</ErrorText>
              </LoaderWrapper>
            ) : sortedData.length === 0 && searchVolumes.trim() ? (
              <LoaderWrapper>
                <ErrorText>No campaigns match "{searchVolumes}"</ErrorText>
              </LoaderWrapper>
            ) : sortedData.length === 0 ? (
              <LoaderWrapper>
                <ErrorText>No campaigns with audience data.</ErrorText>
              </LoaderWrapper>
            ) : (
              <ChartWrapper>
                <SubLabel>
                  {`Page ${page + 1} of ${totalPages} · sorted by audience size`}
                </SubLabel>
                <ResponsiveContainer width="100%" height={380} debounce={50}>
                  <BarChart
                    data={chartData}
                    margin={{ top: 12, right: 24, left: 12, bottom: 80 }}
                  >
                    <XAxis
                      dataKey="campaign_name"
                      interval={0}
                      height={90}
                      tick={xAxisTick}
                    />
                    <YAxis
                      scale="sqrt"
                      domain={[0, "auto"]}
                      tickFormatter={formatNum}
                      tick={{
                        fontSize: 11,
                        fill: "#667085",
                        fontFamily: "var(--font-data)",
                      }}
                      width={56}
                    />
                    <Tooltip
                      content={<VolumeTooltip />}
                      cursor={{ fill: "rgba(0,0,0,0.04)" }}
                    />
                    <Bar
                      dataKey="differential_privacy_count_total"
                      radius={[4, 4, 0, 0]}
                    >
                      {chartData.map((_, idx) => (
                        <Cell
                          key={idx}
                          fill={barColor}
                          fillOpacity={0.75 + (idx % 3) * 0.08}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                {totalPages > 1 && (
                  <Pagination>
                    <PageBtn
                      onClick={() => setPage((p) => p - 1)}
                      disabled={page === 0}
                    >
                      ← Prev
                    </PageBtn>
                    <PageInfo>
                      {page + 1} / {totalPages}
                    </PageInfo>
                    <PageBtn
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page === totalPages - 1}
                    >
                      Next →
                    </PageBtn>
                  </Pagination>
                )}

                {/* #2: zero-volume disclosure */}
                {zeroVolumeCampaigns.length > 0 && (
                  <ZeroRow>
                    <ZeroToggle
                      onClick={() => setShowZeroCampaigns((v) => !v)}
                    >
                      <ZeroChevron open={showZeroCampaigns}>▶</ZeroChevron>
                      {zeroVolumeCampaigns.length} campaign
                      {zeroVolumeCampaigns.length !== 1 ? "s" : ""} with no
                      audience match
                    </ZeroToggle>
                    {showZeroCampaigns && (
                      <ZeroList>
                        {zeroVolumeCampaigns.map((c) => (
                          <ZeroItem key={c.campaign_name}>
                            {c.campaign_name}
                          </ZeroItem>
                        ))}
                      </ZeroList>
                    )}
                  </ZeroRow>
                )}
              </ChartWrapper>
            )}
          </VolumesTab>
        ) : tab === "filter-freq" ? (
          filterFreqData.length === 0 ? (
            <LoaderWrapper>
              <ErrorText>No filter strings found.</ErrorText>
            </LoaderWrapper>
          ) : (
            <FilterTableSection>
              <TableScroll>
                <AnalyticsTable>
                  <colgroup>
                    <col style={{ width: "50%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "36%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <Th>Filter String</Th>
                      <Th $align="center">Appearances</Th>
                      <Th>Campaigns</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterFreqData.map(({ filter, count, campaigns }) => (
                      <tr key={filter}>
                        <Td>
                          <FilterCode title={filter}>{filter}</FilterCode>
                        </Td>
                        <Td $align="center">
                          <CountBadge>{count}</CountBadge>
                        </Td>
                        <Td>
                          <CampaignTags>
                            {campaigns.map((c) => (
                              <CampaignTag key={c}>{c}</CampaignTag>
                            ))}
                          </CampaignTags>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </AnalyticsTable>
              </TableScroll>
            </FilterTableSection>
          )
        ) : (
          // attr-freq tab
          <ChartWrapper>
            {/* #10: attribute search */}
            <AttrSearchRow>
              <SearchInput
                type="search"
                placeholder="Search attributes…"
                value={searchAttr}
                onChange={(e) => setSearchAttr(e.target.value)}
              />
            </AttrSearchRow>

            {attrFreqData.length === 0 ? (
              <LoaderWrapper>
                <ErrorText>No attribute data found.</ErrorText>
              </LoaderWrapper>
            ) : filteredAttrFreqData.length === 0 ? (
              <LoaderWrapper>
                <ErrorText>No attributes match "{searchAttr}"</ErrorText>
              </LoaderWrapper>
            ) : (
              <>
                <SubLabel>
                  Number of filter strings each attribute appears in
                </SubLabel>
                <AttrChartScroll>
                  <BarChart
                    width={Math.max(520, filteredAttrFreqData.length * 100)}
                    height={320}
                    data={filteredAttrFreqData}
                    margin={{ top: 12, right: 16, left: 8, bottom: 100 }}
                  >
                    <XAxis
                      dataKey="attr"
                      interval={0}
                      height={100}
                      tick={xAxisTick}
                    />
                    <YAxis
                      tickFormatter={(v) => `${v}`}
                      tick={{
                        fontSize: 11,
                        fill: "#667085",
                        fontFamily: "var(--font-data)",
                      }}
                      allowDecimals={false}
                      width={36}
                    />
                    <Tooltip
                      content={<AttrTooltip />}
                      cursor={{ fill: "rgba(0,0,0,0.04)" }}
                    />
                    <Bar dataKey="filterCount" radius={[4, 4, 0, 0]}>
                      {filteredAttrFreqData.map((_, idx) => (
                        <Cell
                          key={idx}
                          fill={barColor}
                          fillOpacity={0.75 + (idx % 3) * 0.08}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </AttrChartScroll>
                <AttrTableWrapper>
                  <TableScroll>
                    <AnalyticsTable>
                      <colgroup>
                        <col style={{ width: "50%" }} />
                        <col style={{ width: "25%" }} />
                        <col style={{ width: "25%" }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <Th>Attribute</Th>
                          <Th $align="center">Filter Strings</Th>
                          <Th $align="center">Campaigns</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAttrFreqData.map(
                          ({ attr, filterCount, campaignCount }) => (
                            <tr key={attr}>
                              <Td>
                                <AttrName>{attr}</AttrName>
                              </Td>
                              <Td $align="center">
                                <CountBadge>{filterCount}</CountBadge>
                              </Td>
                              <Td $align="center">
                                <CountBadge secondary>{campaignCount}</CountBadge>
                              </Td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </AnalyticsTable>
                  </TableScroll>
                </AttrTableWrapper>
              </>
            )}
          </ChartWrapper>
        )}
      </Modal>

      {tickTooltip &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: tickTooltip.y,
              left: tickTooltip.x,
              transform: "translateX(-50%) translateY(6px)",
              background: "rgba(0,0,0,0.82)",
              color: "#fff",
              padding: "4px 10px",
              borderRadius: "5px",
              fontSize: "11px",
              fontFamily: "var(--font-data)",
              whiteSpace: "nowrap",
              zIndex: 10000,
              pointerEvents: "none",
              lineHeight: "1.5",
            }}
          >
            {tickTooltip.text}
          </div>,
          document.body,
        )}
    </Backdrop>
  );
};

export default CampaignVolumesModal;

// ─── Layout ────────────────────────────────────────────────────────────────────

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 10px;
  width: 92vw;
  max-width: 1100px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  overflow: hidden;
`;

const Header = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 24px 12px;
  background: var(--Primary-Color-Shade-1);
  border-radius: 10px 10px 0 0;
  flex-shrink: 0;
`;

const HeaderRight = styled.div`
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  font-family: var(--font-label);
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #ffffff;
`;

const TabBar = styled.div`
  display: flex;
  border-bottom: 1px solid #e4e7ec;
  padding: 0 24px;
  flex-shrink: 0;
  gap: 4px;
`;

const TabItem = styled.button<{ active?: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid
    ${({ active }) =>
      active ? "var(--Primary-Color-Shade-1)" : "transparent"};
  color: ${({ active }) =>
    active ? "var(--Primary-Color-Shade-1)" : "#667085"};
  font-size: 13px;
  font-family: var(--font-label);
  font-weight: ${({ active }) => (active ? 600 : 400)};
  padding: 10px 14px;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  &:hover {
    color: var(--Primary-Color-Shade-1);
  }
`;

// ─── Volumes tab ───────────────────────────────────────────────────────────────

const VolumesTab = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
`;

const StatsRow = styled.div`
  display: flex;
  align-items: stretch;
  padding: 14px 24px 12px;
  border-bottom: 1px solid #f2f4f7;
  flex-shrink: 0;
  gap: 0;
`;

const StatCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
`;

const StatDivider = styled.div`
  width: 1px;
  background: #e4e7ec;
  margin: 0 12px;
  align-self: stretch;
`;

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #1a2b45;
  font-family: var(--font-data);
  line-height: 1.1;
  display: flex;
  align-items: baseline;
  gap: 2px;
`;

const StatCountTotal = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #98a2b3;
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: #98a2b3;
  font-family: var(--font-label);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const ChartWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 12px 24px 12px;
  overflow-y: auto;
  min-height: 0;
`;

const SubLabel = styled.div`
  font-size: 11px;
  color: #98a2b3;
  font-family: var(--font-label);
`;

// ─── Zero-volume disclosure (#2) ───────────────────────────────────────────────

const ZeroRow = styled.div`
  margin-top: 12px;
  border-top: 1px solid #f2f4f7;
  padding-top: 10px;
`;

const ZeroToggle = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: var(--font-label);
  font-size: 12px;
  color: #667085;
  display: flex;
  align-items: center;
  gap: 6px;
  &:hover {
    color: #344054;
  }
`;

const ZeroChevron = styled.span<{ open: boolean }>`
  display: inline-block;
  font-size: 9px;
  transition: transform 0.15s;
  transform: ${({ open }) => (open ? "rotate(90deg)" : "rotate(0deg)")};
  color: #98a2b3;
`;

const ZeroList = styled.ul`
  margin: 8px 0 0 20px;
  padding: 0;
  list-style: disc;
`;

const ZeroItem = styled.li`
  font-family: var(--font-data);
  font-size: 12px;
  color: #344054;
  padding: 2px 0;
`;

// ─── Attr search row (#10) ─────────────────────────────────────────────────────

const AttrSearchRow = styled.div`
  padding-bottom: 10px;
  flex-shrink: 0;
`;

// ─── Shared ────────────────────────────────────────────────────────────────────

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1;
  &:hover {
    color: #ffffff;
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 10px 0 4px;
  flex-shrink: 0;
`;

const PageBtn = styled.button<{ disabled?: boolean }>`
  background: none;
  border: 1px solid
    ${({ disabled }) =>
      disabled ? "#d0d5dd" : "var(--Primary-Color-Shade-1)"};
  border-radius: 4px;
  color: ${({ disabled }) =>
    disabled ? "#d0d5dd" : "var(--Primary-Color-Shade-1)"};
  font-size: 12px;
  font-family: var(--font-label);
  padding: 4px 12px;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  transition: background 0.15s ease;
  &:hover:not(:disabled) {
    background: var(--Primary-Color-Shade-1-pale, #e6f1fb);
  }
`;

const PageInfo = styled.span`
  font-size: 12px;
  color: #667085;
  font-family: var(--font-data);
`;

const FilterTableSection = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 12px 24px 16px;
`;

const TableScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  border-radius: 6px;
  background: #fff;
`;

const AttrChartScroll = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  flex-shrink: 0;
`;

const AttrTableWrapper = styled.div`
  margin-top: 12px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const AnalyticsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-family: var(--font-data);
`;

const Th = styled.th<{ $align?: "left" | "center" | "right" }>`
  text-align: ${({ $align }) => $align ?? "left"};
  font-size: 11px;
  font-weight: 700;
  color: var(--Primary-Color-Shade-1, #0d6ab2);
  font-family: var(--font-label);
  background: var(--Primary-Color-Shade-1-pale, #daefff);
  padding: 10px 12px;
  border: none;
  border-bottom: 2px solid #dde8f5;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 2;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
`;

const Td = styled.td<{ $align?: "left" | "center" | "right" }>`
  padding: 10px 12px;
  border-bottom: 1px solid #ddd;
  text-align: ${({ $align }) => $align ?? "left"};
  font-size: 14px;
  color: #101828;
  font-family: var(--font-data);
  overflow: hidden;
`;

const FilterCode = styled.span`
  display: block;
  font-family: "Roboto Mono", "Courier New", monospace;
  font-size: 13px;
  color: #101828;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const CountBadge = styled.span<{ secondary?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  font-family: var(--font-data);
  background: ${({ secondary }) =>
    secondary ? "#e4e7ec" : "rgba(var(--Primary-Color-Shade-1-rgb), 0.12)"};
  color: ${({ secondary }) =>
    secondary ? "#344054" : "var(--Primary-Color-Shade-1)"};
`;

const CampaignTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 100%;
`;

const CampaignTag = styled.span`
  display: inline-block;
  background: #e4e7ec;
  color: #101828;
  font-size: 12px;
  font-family: var(--font-data);
  padding: 3px 8px;
  border-radius: 10px;
  white-space: nowrap;
`;

const AttrName = styled.span`
  font-family: "Roboto Mono", "Courier New", monospace;
  font-size: 12px;
  color: #1a2b45;
`;

const LoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  flex: 1;
`;

const ErrorText = styled.p`
  font-size: 14px;
  color: #667085;
  text-align: center;
  padding: 0 24px;
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid #e4e7ec;
  border-top-color: var(--Primary-Color-Shade-1);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const TooltipBox = styled.div`
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  padding: 8px 12px;
  max-width: 320px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const TooltipName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #1a2b45;
  margin-bottom: 4px;
`;

const TooltipCount = styled.div`
  font-size: 11px;
  color: #344054;
  margin-bottom: 4px;
`;

const TooltipFilterLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  font-family: var(--font-label);
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 3px;
  margin-top: 4px;
`;

const SearchRow = styled.div`
  padding: 8px 24px 0;
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  font-family: var(--font-data);
  font-size: 12px;
  color: #1a202c;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 5px 10px;
  background: #ffffff;
  width: 100%;
  max-width: 280px;
  height: 30px;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:focus {
    outline: none;
    border-color: var(--Primary-Color-Shade-1);
    box-shadow: 0 0 0 3px rgba(var(--Primary-Color-Shade-1-rgb), 0.12);
  }
  &::-webkit-search-cancel-button {
    cursor: pointer;
  }
`;

const TooltipFilter = styled.div`
  font-size: 11px;
  color: #344054;
  line-height: 1.5;
  padding: 1px 0;
  border-left: 2px solid var(--Primary-Color-Shade-1);
  padding-left: 6px;
  margin-bottom: 2px;
  word-break: break-word;
`;
